import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Turnstile } from '@/components/Turnstile';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hi! I'm here to help answer your questions about StudNote. Ask me anything!",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
  const [handoffData, setHandoffData] = useState({
    name: '',
    email: '',
    issue: ''
  });
  const [isHandoffSubmitting, setIsHandoffSubmitting] = useState(false);
  const [handoffComplete, setHandoffComplete] = useState(false);
  const [handoffCaptchaToken, setHandoffCaptchaToken] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const trimmedInput = input.trim();

    if (!trimmedInput || isLoading) return;

    if (trimmedInput.length > 2000) {
      toast({
        title: "Message too long",
        description: "Please keep messages under 2000 characters",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('support-chat', {
        body: {
          message: input,
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: m.content
          }))
        }
      });

      if (error) throw error;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I'm sorry, I'm having trouble connecting right now. Please try again or contact support@studnote.com for help.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleHandoffSubmit = async () => {
    if (!handoffData.name.trim() || !handoffData.email.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide your name and email.",
        variant: "destructive"
      });
      return;
    }

    if (!handoffCaptchaToken) {
      toast({
        title: "CAPTCHA Required",
        description: "Please complete the CAPTCHA verification",
        variant: "destructive"
      });
      return;
    }

    setIsHandoffSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-support-handoff', {
        body: {
          userName: handoffData.name,
          userEmail: handoffData.email,
          issueDescription: handoffData.issue || undefined,
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp.toISOString()
          })),
          captchaToken: handoffCaptchaToken
        }
      });

      if (error) throw error;

      setHandoffComplete(true);
      setHandoffDialogOpen(false);

      const confirmationMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "✅ Your request has been sent to our support team. We'll respond to your email within 1-3 business days. Thank you!",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, confirmationMessage]);

      toast({
        title: "Request Sent",
        description: "Our support team will contact you via email within 1-3 business days.",
      });

      // Reset form
      setHandoffData({ name: '', email: '', issue: '' });
      setHandoffCaptchaToken(null);
    } catch (error) {
      console.error('Handoff error:', error);
      toast({
        title: "Error",
        description: "Failed to send your request. Please try again or email support@studnote.com directly.",
        variant: "destructive"
      });
      setHandoffCaptchaToken(null);
    } finally {
      setIsHandoffSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-2xl hover:scale-110 transition-transform z-50 touch-manipulation"
          size="icon"
          aria-label="Open chat support"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-[90vw] sm:w-96 h-[500px] shadow-2xl z-50 flex flex-col animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <div>
                <h3 className="font-semibold">StudNote Support</h3>
                <p className="text-xs opacity-90">AI-powered help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!handoffComplete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHandoffDialogOpen(true)}
                  className="h-8 hover:bg-primary-foreground/20 text-primary-foreground flex items-center gap-1.5"
                  disabled={isLoading}
                >
                  <UserCircle className="h-4 w-4" />
                  <span className="text-xs">Talk to Human</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 hover:bg-primary-foreground/20 text-primary-foreground"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted text-foreground rounded-lg p-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={handoffComplete ? "Chat with support team via email" : "Ask a question..."}
                disabled={isLoading || handoffComplete}
                maxLength={2000}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={isLoading || !input.trim() || handoffComplete}
                size="icon"
                className="touch-manipulation"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {handoffComplete ? "Request sent to support team" : "Powered by AI • Usually replies instantly"}
            </p>
          </div>
        </Card>
      )}

      {/* Handoff Dialog */}
      <Dialog open={handoffDialogOpen} onOpenChange={setHandoffDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Connect with Human Support</DialogTitle>
            <DialogDescription>
              Our support team will review your conversation and respond via email within 1-3 business days.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Your Name *</Label>
              <Input
                id="name"
                value={handoffData.name}
                onChange={(e) => setHandoffData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                disabled={isHandoffSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Your Email *</Label>
              <Input
                id="email"
                type="email"
                value={handoffData.email}
                onChange={(e) => setHandoffData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                disabled={isHandoffSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="issue">Brief Description (Optional)</Label>
              <Textarea
                id="issue"
                value={handoffData.issue}
                onChange={(e) => setHandoffData(prev => ({ ...prev, issue: e.target.value }))}
                placeholder="Tell us briefly what you need help with..."
                rows={3}
                disabled={isHandoffSubmitting}
              />
            </div>
            <div className="flex justify-center">
              <Turnstile
                onSuccess={(token) => setHandoffCaptchaToken(token)}
                onError={() => setHandoffCaptchaToken(null)}
                onExpire={() => setHandoffCaptchaToken(null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setHandoffDialogOpen(false)}
              disabled={isHandoffSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleHandoffSubmit}
              disabled={isHandoffSubmitting || !handoffCaptchaToken}
            >
              {isHandoffSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
